/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import {
  monthGrid,
  dateKey,
  isSameDay,
} from '@features/agenda/utils/agenda-dates';
import { eventColor } from './event-color';
import { Text, UnstyledButton } from '@/shared/ui';

interface MonthViewProps {
  year: number;
  month: number;
  events: ExpandedEvent[];
  calendars: Calendar[];
  onDayClick: (date: Date) => void;
  onSelect: (event: ExpandedEvent) => void;
}

const WEEKDAY_INDEXES = [0, 1, 2, 3, 4, 5, 6];

const ROW_MIN_HEIGHT = 72;
const DAY_NUMBER_BLOCK = 28;
const CELL_PADDING_Y = 12;
const CHIP_HEIGHT = 22;
const CHIP_GAP = 1;
const OVERFLOW_LINE_HEIGHT = 16;
const MAX_VISIBLE_CHIPS = 3;

/**
 * Reports whether the given date is today.
 * @param day - The date to test.
 * @returns True when `day` falls on the current calendar day.
 */
const isToday = (day: Date): boolean => dayjs().isSame(dayjs(day), 'day');

/**
 * Computes the pixel height a day cell needs to fit its visible chips
 * (capped at {@link MAX_VISIBLE_CHIPS}) plus the overflow line, without
 * relying on CSS auto-sizing (which silently degrades under Grid's
 * intrinsic-sizing quirks — see MonthView row-height fix history).
 *
 * @param eventCount - Total events on that day.
 * @returns Required cell height in pixels, floored at {@link ROW_MIN_HEIGHT}.
 */
const cellHeightFor = (eventCount: number): number => {
  const visibleCount = Math.min(eventCount, MAX_VISIBLE_CHIPS);
  const chipsHeight = visibleCount * CHIP_HEIGHT + Math.max(0, visibleCount - 1) * CHIP_GAP;
  const overflowHeight = eventCount > MAX_VISIBLE_CHIPS ? OVERFLOW_LINE_HEIGHT : 0;
  const needed = DAY_NUMBER_BLOCK + CELL_PADDING_Y + chipsHeight + overflowHeight;
  return Math.max(ROW_MIN_HEIGHT, needed);
};

/**
 * Month grid (6 weeks) with up to three event chips per day cell.
 * Uses a flat grid layout with shared borders (no gap between cells).
 *
 * @param props Year, month, events, calendars and handlers.
 * @returns Rendered month view.
 */
export const MonthView: React.FC<MonthViewProps> = ({
  year,
  month,
  events,
  calendars,
  onDayClick,
  onSelect,
}) => {
  const { t } = useTranslation('features/agenda');
  const cells = monthGrid(year, month);
  const rows = cells.length / 7;
  const eventCounts = cells.map(
    (cell) => events.filter((e) => isSameDay(e.start_at_utc, cell.date)).length
  );
  const rowHeights = Array.from({ length: rows }, (_, rowIdx) =>
    Math.max(
      ...eventCounts.slice(rowIdx * 7, rowIdx * 7 + 7).map(cellHeightFor)
    )
  );
  return (
    <div
      data-testid="agenda-month-view"
      className="flex min-h-0 flex-1 flex-col rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-[var(--shadow-sm)]"
    >
      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-[var(--border-subtle)]">
        {WEEKDAY_INDEXES.map((i) => (
          <div
            key={i}
            className="py-(--space-2) text-center text-xs font-bold uppercase tracking-wider text-[var(--text-tertiary)]"
            style={{ borderLeft: i > 0 ? '1px solid var(--border-ultra-subtle)' : undefined }}
          >
            {t(`weekday_short.${i}`)}
          </div>
        ))}
      </div>

      {/* Day cells — each row's minimum height is computed in JS from its
          busiest day (see cellHeightFor). The pixel value is the track's
          floor, not its size: a bare `auto`/`1fr` track can silently shrink
          below its content height once a descendant has `overflow-hidden`
          (it loses its content-based minimum), which crushed chips into
          unreadable slivers on busy days. Pairing that floor with `1fr` lets
          a short month share the leftover height instead of leaving a dead
          band under the last week, while a busy month stays at the floor and
          scrolls. */}
      <div
        className="grid min-h-0 flex-1 shrink-0 grid-cols-7 overflow-y-auto"
        style={{
          gridTemplateRows: rowHeights.map((h) => `minmax(${h}px, 1fr)`).join(' '),
        }}
      >
        {cells.map((cell, idx) => {
          const dayEvents = events.filter((e) =>
            isSameDay(e.start_at_utc, cell.date)
          );
          const visible = dayEvents.slice(0, 3);
          const overflow = dayEvents.length - visible.length;
          const today = isToday(cell.date);
          const isWeekend = cell.date.getDay() === 0 || cell.date.getDay() === 6;
          return (
            <div
              key={dateKey(cell.date)}
              data-testid="agenda-month-cell"
              onClick={() => onDayClick(cell.date)}
              className="flex cursor-pointer flex-col gap-(--space-0-5) overflow-hidden border-b border-[var(--border-ultra-subtle)] p-(--space-1-5) transition-colors hover:bg-[var(--surface-secondary)]"
              style={{
                borderLeft: idx % 7 > 0 ? '1px solid var(--border-ultra-subtle)' : undefined,
                background: isWeekend
                  ? 'color-mix(in srgb, var(--surface-secondary) 50%, var(--surface-primary))'
                  : undefined,
                opacity: cell.isCurrentMonth ? 1 : 0.3,
              }}
            >
              {/* Day number */}
              <div
                className="mb-(--space-1) flex h-6 w-6 items-center justify-center rounded-full text-(--text-xs)"
                style={{
                  background: today ? 'var(--seed-base)' : 'transparent',
                  color: today ? '#fff' : 'var(--text-primary)',
                  fontWeight: today ? 700 : 500,
                }}
              >
                {cell.date.getDate()}
              </div>

              {/* Event chips */}
              <div className="flex flex-col gap-px">
                {visible.map((ev) => {
                  const color = eventColor(ev, calendars);
                  return (
                    <UnstyledButton
                      key={`${ev.event_id}-${ev.occurrence_start}`}
                      type="button"
                      data-testid="agenda-event-chip"
                      title={ev.title}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(ev);
                      }}
                      className="relative block w-full truncate rounded-[var(--radius-sm)] px-(--space-1-5) py-(--space-1) text-left text-xs font-bold leading-tight transition-shadow hover:shadow-[var(--shadow-sm)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)]"
                      style={{
                        // Inline, for the same reason as WeekColumn: the shared
                        // component's own classes outrank `font-bold`.
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        background: `color-mix(in srgb, ${color} 14%, var(--surface-primary))`,
                        borderLeft: `2px solid ${color}`,
                        color,
                      }}
                    >
                      {ev.title}
                      {Array.isArray(ev.recurrence) && ev.recurrence.length > 0 && (
                        <Text component="span"
                          data-testid="recurrence-indicator"
                          aria-label={t('preview.recurring')}
                          className="ml-(--space-0-5) inline-flex align-middle"
                        >
                          <svg
                            width="9"
                            height="9"
                            viewBox="0 0 15 15"
                            fill="none"
                            aria-hidden="true"
                            className="opacity-60"
                          >
                            <path
                              d="M3.5 2a.5.5 0 0 1 .5.5v1.77A5.5 5.5 0 0 1 13 7.5a.5.5 0 0 1-1 0A4.5 4.5 0 0 0 4.58 5H6.5a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5v-3a.5.5 0 0 1 .5-.5ZM2 7.5a.5.5 0 0 1 1 0A4.5 4.5 0 0 0 10.42 10H8.5a.5.5 0 0 1 0-1h3a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0V10.73A5.5 5.5 0 0 1 2 7.5Z"
                              fill="currentColor"
                            />
                          </svg>
                        </Text>
                      )}
                    </UnstyledButton>
                  );
                })}
                {overflow > 0 && (
                  <div className="px-(--space-1-5) text-xs font-medium text-[var(--text-tertiary)]">
                    {t('month.more', { count: overflow })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
